import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  Input,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorMode,
  useColorModeValue,
  useBreakpointValue,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import {
  MoonIcon,
  StarIcon,
  SunIcon,
  TriangleDownIcon,
  TriangleUpIcon,
} from "@chakra-ui/icons";
import { MapPin } from "lucide-react";
import { Sparklines, SparklinesLine } from "react-sparklines";
import "./flags.scss";
import "./App.css";

const formatDateLabel = (dateValue) => {
  if (!dateValue || dateValue.length !== 8) return dateValue;
  const year = Number(dateValue.slice(0, 4));
  const month = Number(dateValue.slice(4, 6));
  const day = Number(dateValue.slice(6, 8));
  if (!year || !month || !day) return dateValue;
  const dateObj = new Date(Date.UTC(year, month - 1, day));
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(dateObj);
};

const truncateText = (value, maxLength = 52) => {
  if (!value) return "-";
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 1)}...`;
};

const simplifyEventLabel = (value) => {
  if (!value) return "-";
  return value.replace(/^(Men|Women)\s+Elite\s*-\s*/i, "");
};

const parseCompactDate = (value) => {
  if (!value || String(value).length !== 8) return null;
  const year = Number(String(value).slice(0, 4));
  const month = Number(String(value).slice(4, 6));
  const day = Number(String(value).slice(6, 8));
  if (!year || !month || !day) return null;
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeDateKey = (value) => {
  if (value == null) return "";
  return String(value).replace(/\D/g, "");
};

const getNationalityChangeForDate = (rider, dateValue) => {
  if (!rider || !dateValue || !Array.isArray(rider.nationality_changes)) {
    return [];
  }

  const targetDateKey = normalizeDateKey(dateValue);
  return rider.nationality_changes.filter(
    (change) => normalizeDateKey(change.date) === targetDateKey,
  );
};

const getAllNationalityChanges = (rider) => {
  if (!rider || !Array.isArray(rider.nationality_changes)) return [];
  return [...rider.nationality_changes].sort((a, b) =>
    // sort newest first by normalized date
    normalizeDateKey(b.date).localeCompare(normalizeDateKey(a.date)),
  );
};

const parseCompetitionEndDate = (dateStr) => {
  if (!dateStr) return null;
  const value = String(dateStr).trim();
  const parts = value
    .split(/[-–—]/)
    .map((part) => part.trim())
    .filter(Boolean);
  let candidate = parts[parts.length - 1] || value;
  if (!/\d{4}/.test(candidate)) {
    const yearMatch = value.match(/(\d{4})/);
    if (yearMatch) {
      candidate = `${candidate} ${yearMatch[1]}`.trim();
    }
  }
  const parsed = Date.parse(candidate);
  return Number.isNaN(parsed) ? null : new Date(parsed);
};

const getHighlightKey = (result) =>
  result.event_id ||
  [
    result.competition_title,
    result.competition_dates,
    result.competition_class,
    result.event,
  ]
    .filter(Boolean)
    .join("|");

const getHighlightType = (competitionClass, rank) => {
  // CN national champion (rank 1) shows a crown; podiums are medals
  if (competitionClass === "CN" && rank === 1) return "champion";
  // UCI World Champion (class CM rank 1) shows the rainbow stripes
  if (competitionClass === "CM" && rank === 1) return "world";
  // Treat CN (national championships) podiums as medals as well
  if (competitionClass === "CN" && rank <= 3) return "medal";
  if (["CC", "CM", "JO", "JR"].includes(competitionClass) && rank <= 3)
    return "medal";
  return "result";
};

const getHighlightScore = (rank, stars, competitionClass) => {
  const safeRank = Number.isFinite(rank) ? Math.min(rank, 20) : 20;
  const safeStars = Number.isFinite(stars) ? Math.max(0, stars) : 0;
  const rankScore = Math.max(0, 12 - safeRank) * 7;
  const starScore = safeStars * 12;
  const classBonus =
    competitionClass === "CN"
      ? 8
      : ["CC", "CM", "JO", "JR"].includes(competitionClass)
        ? 10
        : 0;
  return rankScore + starScore + classBonus;
};

const getRecentHighlights = (results, referenceDate) => {
  if (!Array.isArray(results) || !results.length) return [];

  const anchorDate = referenceDate || new Date();
  const cutoffDate = new Date(anchorDate);
  cutoffDate.setUTCFullYear(cutoffDate.getUTCFullYear() - 1);

  const recentResults = results
    .map((result, index) => {
      const rank = toNumberRank(result.rank);
      const stars =
        typeof result.event_stars === "number"
          ? result.event_stars
          : Number(result.event_stars);
      const competitionClass = String(result.competition_class || "").trim();
      const endDate = parseCompetitionEndDate(
        result.competition_dates || result.competition_dates_text || "",
      );

      return {
        index,
        key: getHighlightKey(result),
        result,
        rank,
        stars: Number.isFinite(stars) ? stars : 0,
        competitionClass,
        endDate,
        type: getHighlightType(competitionClass, rank),
      };
    })
    .filter(
      (entry) =>
        entry.rank !== null &&
        entry.endDate &&
        entry.endDate >= cutoffDate &&
        entry.endDate <= anchorDate,
    );

  if (!recentResults.length) return [];

  const byPriority = (a, b) => {
    if (a.endDate && b.endDate && a.endDate.getTime() !== b.endDate.getTime()) {
      return b.endDate.getTime() - a.endDate.getTime();
    }
    if (a.rank !== b.rank) return a.rank - b.rank;
    return a.index - b.index;
  };

  const selected = new Map();

  recentResults
    .filter(
      (entry) =>
        (entry.competitionClass === "CN" && entry.rank <= 3) ||
        (["CC", "CM", "JO", "JR"].includes(entry.competitionClass) &&
          entry.rank <= 3),
    )
    .sort(byPriority)
    .forEach((entry) => {
      if (!selected.has(entry.key)) {
        selected.set(entry.key, {
          ...entry,
          score: getHighlightScore(
            entry.rank,
            entry.stars,
            entry.competitionClass,
          ),
        });
      }
    });

  if (selected.size < 3) {
    recentResults
      .filter((entry) => !selected.has(entry.key))
      .map((entry) => ({
        ...entry,
        score: getHighlightScore(
          entry.rank,
          entry.stars,
          entry.competitionClass,
        ),
      }))
      .filter((entry) => entry.score >= 70)
      .sort((a, b) => {
        if (a.score !== b.score) return b.score - a.score;
        return byPriority(a, b);
      })
      .slice(0, 3 - selected.size)
      .forEach((entry) => {
        selected.set(entry.key, entry);
      });
  }

  return Array.from(selected.values()).sort((a, b) => {
    if (a.type !== b.type) {
      const order = { medal: 0, result: 1 };
      return order[a.type] - order[b.type];
    }
    if (a.score !== b.score) return b.score - a.score;
    return byPriority(a, b);
  });
};

const HighlightMarker = ({ type, rank }) => {
  const bronzeBg = "yellow.700";
  const worldStroke = useColorModeValue("gray.200", "whiteAlpha.200");
  if (type === "champion") {
    return React.createElement(
      Box,
      { position: "relative", w: "28px", h: "24px", flexShrink: 0 },
      React.createElement(Box, {
        position: "absolute",
        inset: 0,
        bg: "yellow.400",
        border: "1px solid",
        borderColor: "yellow.500",
        clipPath:
          "polygon(0 100%, 0 42%, 17% 56%, 31% 10%, 50% 42%, 69% 10%, 83% 56%, 100% 42%, 100% 100%)",
        borderRadius: "4px",
      }),
    );
  }

  if (type === "world") {
    // World champion stripes inside a white circular badge
    return (
      <Box w="28px" h="28px" flexShrink={0}>
        <svg
          viewBox="0 0 28 28"
          width="28"
          height="28"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <clipPath id="world-clip">
              <circle cx="14" cy="14" r="13" />
            </clipPath>
          </defs>
          <circle
            cx="14"
            cy="14"
            r="13"
            fill="white"
            stroke={worldStroke}
            strokeWidth="1"
          />
          <g clipPath="url(#world-clip)">
            {/* center the five stripes across the middle with white padding left/right */}
            <rect x="1" y="4" width="26" height="4" rx="1" fill="#35b24a" />
            <rect x="1" y="8" width="26" height="4" rx="1" fill="#f8df00" />
            <rect x="1" y="12" width="26" height="4" rx="1" fill="#000000" />
            <rect x="1" y="16" width="26" height="4" rx="1" fill="#c7203c" />
            <rect x="1" y="20" width="26" height="4" rx="1" fill="#1e76bd" />
          </g>
        </svg>
      </Box>
    );
  }

  const medalStyles = {
    1: { bg: "yellow.400", borderColor: "yellow.500", color: "gray.900" },
    2: { bg: "gray.300", borderColor: "gray.400", color: "gray.900" },
    3: { bg: bronzeBg, borderColor: "yellow.900", color: "white" },
  };
  const fallbackStyle = {
    bg: "gray.500",
    borderColor: "gray.600",
    color: "white",
  };
  const style =
    type === "medal" ? medalStyles[rank] || fallbackStyle : fallbackStyle;

  return (
    <Flex
      w="28px"
      h="28px"
      borderRadius="full"
      align="center"
      justify="center"
      bg={style.bg}
      border="1px solid"
      borderColor={style.borderColor}
      color={style.color}
      fontWeight="700"
      fontSize="xs"
      flexShrink={0}
    >
      {rank}
    </Flex>
  );
};

const StarRating = ({ value = 0, max = 5, size = "sm" }) => {
  const starBoxSize = size === "xs" ? "12px" : size === "sm" ? "16px" : "20px";
  return (
    <HStack spacing={1} align="center" lineHeight="1">
      {Array.from({ length: max }).map((_, i) => {
        const idx = i + 1;
        const raw = Math.max(0, Math.min(1, value - (idx - 1)));
        const pct = Math.round(raw * 100);
        return (
          <Box
            key={i}
            position="relative"
            w={starBoxSize}
            h={starBoxSize}
            lineHeight="1"
            overflow="hidden"
          >
            <Icon
              as={StarIcon}
              boxSize={starBoxSize}
              color="gray.300"
              display="block"
            />
            <Box
              position="absolute"
              left={0}
              top={0}
              w={`${pct}%`}
              h="100%"
              overflow="hidden"
            >
              <Icon
                as={StarIcon}
                boxSize={starBoxSize}
                color="orange.400"
                display="block"
              />
            </Box>
          </Box>
        );
      })}
    </HStack>
  );
};

const toNumberRank = (value) => {
  if (value == null || value === "-") return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const invertRanksForSparkline = (values) => {
  if (!values.length) return [];
  const minRank = Math.min(...values);
  const maxRank = Math.max(...values);
  return values.map((value) =>
    value === null ? null : maxRank - value + minRank,
  );
};

const normalizeNationalities = (items) => {
  if (!Array.isArray(items)) return [];
  const normalized = items
    .map((item) => {
      if (typeof item === "string") {
        return { value: item, label: item };
      }
      if (item && typeof item === "object") {
        const value =
          item.ioc ||
          item.value ||
          item.nationality_ioc ||
          item.nationality ||
          item.name ||
          item.nationality_name;
        const label =
          item.name ||
          item.nationality_name ||
          item.label ||
          item.ioc ||
          item.value;
        const continent = item.continent || null;
        if (value && label) {
          return { value, label, continent };
        }
      }
      return null;
    })
    .filter(Boolean);
  const unique = new Map();
  normalized.forEach((item) => unique.set(item.value, item));
  return Array.from(unique.values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
};

function App() {
  const { colorMode, toggleColorMode } = useColorMode();
  const apiBase = process.env.PARCEL_APP_API_BASE || "";
  const staticDataCache = useRef({});
  const fetchGenerationRef = useRef(0);
  const modalBodyRef = useRef(null);
  const modalTouchRef = useRef({
    isDragging: false,
    startY: 0,
    startScrollTop: 0,
  });
  const [gender, setGender] = useState("men");
  const [nationality, setNationality] = useState("All");
  const [nationalities, setNationalities] = useState([]);
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRider, setSelectedRider] = useState(null);
  const [riderImageUrl, setRiderImageUrl] = useState("");
  const [hasRiderImage, setHasRiderImage] = useState(false);
  const [resultSortOrder, setResultSortOrder] = useState("desc"); // 'desc' = newest first
  const [rankMode, setRankMode] = useState("relative");
  const [showU23, setShowU23] = useState(false);
  const [showSprint, setShowSprint] = useState(true);
  const [showKeirin, setShowKeirin] = useState(true);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [eventRiders, setEventRiders] = useState([]);
  const [eventMeta, setEventMeta] = useState({
    competition: null,
    event: null,
  });
  const [eventLoading, setEventLoading] = useState(false);
  const openResultModal = (result) => {
    setSelectedResult(result);
    setResultModalOpen(true);
    setEventMeta({
      competition: {
        uid: result.competition_id || null,
        title: result.competition_title || null,
        country_ioc: result.competition_country_ioc || null,
        dates: result.competition_dates || null,
        class: result.competition_class || null,
        city: result.competition_city || null,
      },
      event: {
        event_id: result.event_id || null,
        name: result.event || null,
        rating:
          typeof result.event_rating === "number" ? result.event_rating : null,
        stars:
          typeof result.event_stars === "number" ? result.event_stars : null,
      },
    });
    // fetch per-event JSON for full results
    (async () => {
      setEventLoading(true);
      setEventRiders([]);
      try {
        const compUid =
          result.competition_id ||
          result.competition_uid ||
          (result.competition && result.competition.uid) ||
          null;
        const eventId = result.event_id || result.event || null;
        if (compUid && eventId) {
          const normalize = (v) => (v == null ? null : String(v).trim());
          const compStr = normalize(compUid);
          const eventStr = normalize(eventId);
          const compNum =
            compStr && Number(compStr) ? String(Number(compStr)) : null;
          const eventNum =
            eventStr && Number(eventStr) ? String(Number(eventStr)) : null;

          const candidates = [];
          // prefer leading slash absolute paths
          if (compStr && eventStr)
            candidates.push(`/json/events/${compStr}/${eventStr}.json`);
          if (compNum && eventStr)
            candidates.push(`/json/events/${compNum}/${eventStr}.json`);
          if (compStr && eventNum)
            candidates.push(`/json/events/${compStr}/${eventNum}.json`);
          if (compNum && eventNum)
            candidates.push(`/json/events/${compNum}/${eventNum}.json`);
          // also try relative paths without leading slash
          if (compStr && eventStr)
            candidates.push(`json/events/${compStr}/${eventStr}.json`);
          if (compNum && eventStr)
            candidates.push(`json/events/${compNum}/${eventStr}.json`);
          if (compStr && eventNum)
            candidates.push(`json/events/${compStr}/${eventNum}.json`);
          if (compNum && eventNum)
            candidates.push(`json/events/${compNum}/${eventNum}.json`);

          let resp = null;
          for (const p of candidates) {
            try {
              resp = await fetch(p);
              console.debug("Tried event path", p, "status", resp.status);
              if (resp.ok) break;
            } catch (err) {
              console.debug(
                "Fetch error for",
                p,
                err && err.message ? err.message : err,
              );
            }
          }

          if (resp && resp.ok) {
            const data = await resp.json();
            try {
              if (data && (data.competition || data.event)) {
                setEventMeta((prev) => ({
                  competition: {
                    ...prev.competition,
                    ...(data.competition || {}),
                  },
                  event: data.event || prev.event,
                }));
              }
              // Accept several possible shapes produced historically
              let riders = [];
              if (Array.isArray(data.riders)) riders = data.riders;
              else if (Array.isArray(data.results)) riders = data.results;
              else if (data.event && Array.isArray(data.event.riders))
                riders = data.event.riders;
              else if (data.riders && Array.isArray(data.riders.riders))
                riders = data.riders.riders;
              else if (Array.isArray(data)) riders = data;
              else {
                // if nothing matched, log available keys for debugging
                console.debug(
                  "Event JSON loaded but no riders found",
                  Object.keys(data || {}),
                );
              }
              setEventRiders(riders);
            } catch (e) {
              console.error("Error parsing event JSON", e);
              setEventRiders([]);
            }
          } else {
            setEventRiders([]);
          }
        } else {
          setEventRiders([]);
        }
      } catch (e) {
        console.error("Event fetch error", e);
        setEventRiders([]);
      }
      setEventLoading(false);
    })();
  };
  const closeResultModal = () => {
    setResultModalOpen(false);
    setSelectedResult(null);
    setEventMeta({ competition: null, event: null });
  };
  const drawer = useDisclosure();
  const panelBg = useColorModeValue("white", "gray.800");
  const panelBorderColor = useColorModeValue("gray.200", "whiteAlpha.300");
  const mutedTextColor = useColorModeValue("gray.600", "gray.300");
  const subtleTextColor = useColorModeValue("gray.500", "gray.400");
  const activeTextColor = useColorModeValue("gray.900", "white");
  const tableHeaderBg = useColorModeValue("white", "gray.800");
  const tableRowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const selectedRowBg = useColorModeValue("blue.200", "blue.700");
  const sparklineBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const placeholderFlagBg = useColorModeValue("gray.100", "whiteAlpha.200");
  const rankBadgeFallbackBorderColor = useColorModeValue(
    "gray.300",
    "gray.700",
  );
  const rankBadgeFallbackColor = useColorModeValue(
    "gray.800",
    "whiteAlpha.900",
  );
  const sparklineWidth = useBreakpointValue({ base: 150, sm: 170, md: 190 });

  useEffect(() => {
    const fetchGeneration = ++fetchGenerationRef.current;
    const isCurrentFetch = () =>
      fetchGeneration === fetchGenerationRef.current;

    const fetchMeta = async () => {
      setLoading(true);
      try {
        if (process.env.REACT_APP_DATA_MODE === "static") {
          if (!staticDataCache.current[gender]) {
            const response = await fetch(`/json/weekly/${gender}.json`);
            staticDataCache.current[gender] = await response.json();
          }
          const data = staticDataCache.current[gender] || {};
          const riderList = Object.values(data).map((rider) => ({
            name: rider.name,
            birthyear: rider.birthyear,
            nationality_name: rider.nationality,
            nationality_ioc: rider.nationality,
            uci_id: rider.uci_id,
            ranks: rider.weekly || {},
            results: rider.results || [],
          }));
          const dateSet = new Set();
          riderList.forEach((rider) => {
            Object.keys(rider.ranks).forEach((dateValue) =>
              dateSet.add(dateValue),
            );
          });
          const sortedDates = Array.from(dateSet).sort();
          const uniqueNationalityIocs = [
            ...new Set(
              riderList.map((rider) => rider.nationality_ioc).filter(Boolean),
            ),
          ];

          // Load full nationalities.json to enrich rider-present IOC codes with name+continent
          let natLookup = new Map();
          try {
            const natResp = await fetch("/json/nationalities.json");
            if (natResp.ok) {
              const natData = await natResp.json();
              natData.forEach((n) => natLookup.set(n.ioc, n));
            }
          } catch (_) {}

          const nationalitiesData = uniqueNationalityIocs.map((ioc) => {
            const info = natLookup.get(ioc) || {};
            return {
              ioc,
              name: info.name || ioc,
              continent: info.continent || null,
            };
          });

          if (!isCurrentFetch()) return;

          setDates(sortedDates);
          setSelectedDate(
            sortedDates.length ? sortedDates[sortedDates.length - 1] : "",
          );
          setNationalities(normalizeNationalities(nationalitiesData));
          setRiders(riderList);
          setNationality("All");
        } else {
          const [datesResponse, nationalitiesResponse, ridersResponse] =
            await Promise.all([
              fetch(`${apiBase}/api/dates?gender=${gender}`),
              fetch(`${apiBase}/api/nationalities?gender=${gender}`),
              fetch(`${apiBase}/api/rankings?gender=${gender}`),
            ]);
          const datesData = await datesResponse.json();
          const nationalitiesData = await nationalitiesResponse.json();
          const ridersData = await ridersResponse.json();

          const sortedDates = Array.isArray(datesData)
            ? [...datesData].sort()
            : [];
          setDates(sortedDates);
          setSelectedDate(
            sortedDates.length ? sortedDates[sortedDates.length - 1] : "",
          );
          setNationalities(normalizeNationalities(nationalitiesData));

          const riderList = Object.entries(ridersData || {}).map(
            ([name, info]) => ({
              name,
              birthyear: info.birthyear,
              nationality_name: info.nationality_name || info.nationality,
              nationality_ioc: info.nationality_ioc || info.nationality,
              uci_id: info.uci_id,
              ranks: info.ranks || {},
              results: info.results || [],
            }),
          );

          if (!isCurrentFetch()) return;

          setRiders(riderList);
          setNationality("All");
        }
      } catch (error) {
        if (isCurrentFetch()) {
          console.error("Error fetching data:", error);
        }
      } finally {
        if (isCurrentFetch()) {
          setLoading(false);
        }
      }
    };

    fetchMeta();
  }, [gender]);

  useEffect(() => {
    if (!selectedRider || !selectedRider.uci_id) {
      setRiderImageUrl("");
      setHasRiderImage(false);
      return;
    }

    const imageUrl = `https://tissottiming.z6.web.core.windows.net/riders/national/${selectedRider.uci_id}.png`;
    setRiderImageUrl(imageUrl);
    setHasRiderImage(false);

    let isCancelled = false;
    const image = new window.Image();
    image.onload = () => {
      if (!isCancelled) {
        setHasRiderImage(true);
      }
    };
    image.onerror = () => {
      if (!isCancelled) {
        setHasRiderImage(false);
      }
    };
    image.src = imageUrl;

    return () => {
      isCancelled = true;
    };
  }, [selectedRider ? selectedRider.uci_id : null]);

  const previousDate = useMemo(() => {
    if (!selectedDate) return null;
    const index = dates.indexOf(selectedDate);
    if (index > 0) return dates[index - 1];
    return null;
  }, [dates, selectedDate]);

  const CONTINENT_ORDER = [
    "Africa",
    "Asia",
    "Europe",
    "Oceania",
    "Pan America",
  ];

  const continentMap = useMemo(() => {
    const map = new Map();
    nationalities.forEach((n) => {
      if (n.continent) map.set(n.value, n.continent);
    });
    return map;
  }, [nationalities]);

  const nationalitiesByContinent = useMemo(() => {
    const grouped = {};
    const ungrouped = [];
    nationalities.forEach((n) => {
      if (n.continent) {
        if (!grouped[n.continent]) grouped[n.continent] = [];
        grouped[n.continent].push(n);
      } else {
        ungrouped.push(n);
      }
    });
    return { grouped, ungrouped };
  }, [nationalities]);

  const currentYear = new Date().getFullYear();

  const filteredRiders = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return riders.filter((rider) => {
      if (showU23) {
        const age = rider.birthyear ? currentYear - rider.birthyear : null;
        if (age === null || age >= 23) return false;
      }
      if (nationality !== "All") {
        if (nationality.startsWith("continent:")) {
          const cont = nationality.slice("continent:".length);
          if (continentMap.get(rider.nationality_ioc) !== cont) return false;
        } else {
          if (rider.nationality_ioc !== nationality) return false;
        }
      }
      if (term && !rider.name.toLowerCase().includes(term)) {
        return false;
      }
      return true;
    });
  }, [riders, nationality, searchTerm, continentMap, showU23, currentYear]);

  const rankedRiders = useMemo(() => {
    return filteredRiders
      .map((rider) => {
        const rank = toNumberRank(rider.ranks?.[selectedDate]);
        const previousRank = toNumberRank(rider.ranks?.[previousDate]);
        const change =
          rank !== null && previousRank !== null ? previousRank - rank : null;
        return {
          ...rider,
          rank,
          change,
        };
      })
      .filter((rider) => rider.rank !== null)
      .sort((a, b) => {
        if (a.rank !== b.rank) return a.rank - b.rank;
        return a.name.localeCompare(b.name);
      });
  }, [filteredRiders, selectedDate, previousDate]);

  const handleRiderClick = async (rider) => {
    setSelectedRider(rider);
    drawer.onOpen();

    if (!rider?.uci_id) {
      return;
    }

    try {
      const detailUrl =
        process.env.REACT_APP_DATA_MODE === "static"
          ? `/json/rider/${rider.uci_id}.json`
          : `${apiBase}/api/rider/${rider.uci_id}`;
      const response = await fetch(detailUrl);
      if (!response.ok) {
        return;
      }
      const detail = await response.json();
      const detailRankings = detail.rankings || detail.weekly || {};

      // Replace or extend the selected rider with the fetched detail results.
      setSelectedRider((previous) => {
        const current =
          previous && previous.uci_id === rider.uci_id ? previous : rider;
        return {
          ...current,
          name: detail.name || current.name,
          birthyear: detail.birthyear || current.birthyear,
          nationality_name: detail.nationality_name || current.nationality_name,
          nationality_ioc:
            detail.nationality_ioc ||
            detail.nationality ||
            current.nationality_ioc,
          ranks:
            Object.keys(detailRankings).length > 0
              ? detailRankings
              : current.ranks,
          results: detail.results || [],
          nationality_changes: Array.isArray(detail.nationality_changes)
            ? detail.nationality_changes
            : [],
        };
      });
    } catch (error) {
      console.error("Error fetching rider detail:", error);
    }
  };

  const getSparklineData = (rider) => {
    if (!rider || !dates.length) return [];
    const values = dates
      .map((dateValue) => toNumberRank(rider.ranks?.[dateValue]))
      .filter((value) => value !== null);
    return invertRanksForSparkline(values);
  };

  const getBestRankInfo = (rider) => {
    if (!rider) return { rank: null, date: null };
    const entries = Object.entries(rider.ranks || {})
      .map(([dateValue, rankValue]) => ({
        dateValue,
        rank: toNumberRank(rankValue),
      }))
      .filter((entry) => entry.rank !== null);
    if (!entries.length) return { rank: null, date: null };
    entries.sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      return a.dateValue.localeCompare(b.dateValue);
    });
    return { rank: entries[0].rank, date: entries[0].dateValue };
  };

  const recentHighlights = useMemo(() => {
    const referenceDate = parseCompactDate(selectedDate) || new Date();
    return getRecentHighlights(selectedRider?.results || [], referenceDate);
  }, [selectedDate, selectedRider]);

  const selectedNationalityChanges = useMemo(
    () => getAllNationalityChanges(selectedRider),
    [selectedRider],
  );

  // Touch handlers to emulate scrolling inside ModalBody on browsers
  const onModalTouchStart = (e) => {
    const el = modalBodyRef.current;
    if (!el) return;
    modalTouchRef.current.isDragging = true;
    modalTouchRef.current.startY = e.touches ? e.touches[0].clientY : e.clientY;
    modalTouchRef.current.startScrollTop = el.scrollTop;
  };

  const onModalTouchMove = (e) => {
    const el = modalBodyRef.current;
    if (!el || !modalTouchRef.current.isDragging) return;
    const currentY = e.touches ? e.touches[0].clientY : e.clientY;
    const delta = modalTouchRef.current.startY - currentY;
    el.scrollTop = modalTouchRef.current.startScrollTop + delta;
    e.preventDefault();
  };

  const onModalTouchEnd = () => {
    modalTouchRef.current.isDragging = false;
  };

  return (
    <Box minH="100vh" py={{ base: 8, md: 12 }}>
      <Container maxW="6xl">
        <Stack spacing={6}>
          <Stack spacing={3}>
            <Flex
              justify="space-between"
              align={{ base: "flex-start", md: "center" }}
              gap={4}
              direction={{ base: "column", md: "row" }}
            >
              <Heading size="lg">UCI Weekly Sprint Rankings</Heading>
              <HStack
                bg={panelBg}
                px={3}
                py={2}
                borderRadius="md"
                border="1px solid"
                borderColor={panelBorderColor}
                position={{ base: "fixed", md: "static" }}
                top={{
                  base: "max(12px, env(safe-area-inset-top))",
                  md: "auto",
                }}
                right={{ base: "12px", md: "auto" }}
                zIndex={{ base: "popover", md: "auto" }}
              >
                <Icon
                  as={SunIcon}
                  color={colorMode === "light" ? "orange.400" : subtleTextColor}
                />
                <Switch
                  isChecked={colorMode === "dark"}
                  onChange={toggleColorMode}
                  colorScheme="orange"
                  aria-label="Toggle between light and dark mode"
                />
                <Icon
                  as={MoonIcon}
                  color={colorMode === "dark" ? "blue.300" : subtleTextColor}
                />
              </HStack>
            </Flex>
            <Text maxW="2xl" color={mutedTextColor}>
              Explore the most recent sprint rankings and history with quick
              filters and search.
            </Text>
          </Stack>

          <Flex
            gap={{ base: 2, md: 4 }}
            direction="row"
            wrap={{ base: "wrap", md: "nowrap" }}
            align={{ base: "stretch", md: "center" }}
          >
            <HStack
              bg={panelBg}
              px={{ base: 2, md: 4 }}
              py={2}
              borderRadius="md"
              border="1px solid"
              borderColor={panelBorderColor}
              flex={{ base: "1 1 0", md: "0 0 auto" }}
              minW={{ base: "0", md: "110px" }}
              maxW={{ base: "none", md: "110px" }}
              justify="space-between"
            >
              <Text
                fontSize={{ base: "sm", md: "md" }}
                fontWeight={gender === "men" ? "700" : "500"}
                color={gender === "men" ? activeTextColor : subtleTextColor}
              >
                M
              </Text>
              <Switch
                isChecked={gender === "women"}
                onChange={(event) =>
                  setGender(event.target.checked ? "women" : "men")
                }
                colorScheme="pink"
                aria-label="Toggle between men and women rankings"
              />
              <Text
                fontSize={{ base: "sm", md: "md" }}
                fontWeight={gender === "women" ? "700" : "500"}
                color={gender === "women" ? activeTextColor : subtleTextColor}
              >
                F
              </Text>
            </HStack>

            <HStack
              bg={panelBg}
              px={{ base: 2, md: 4 }}
              py={2}
              borderRadius="md"
              border="1px solid"
              borderColor={panelBorderColor}
              flex={{ base: "1 1 0", md: "0 0 auto" }}
              minW={{ base: "0", md: "140px" }}
              maxW={{ base: "none", md: "140px" }}
              justify="space-between"
            >
              <Text
                fontSize={{ base: "sm", md: "md" }}
                fontWeight={!showU23 ? "700" : "500"}
                color={!showU23 ? activeTextColor : subtleTextColor}
              >
                All
              </Text>
              <Switch
                isChecked={showU23}
                onChange={(event) => setShowU23(event.target.checked)}
                colorScheme="teal"
                aria-label="Toggle U23 filter"
              />
              <Text
                fontSize={{ base: "sm", md: "md" }}
                fontWeight={showU23 ? "700" : "500"}
                color={showU23 ? activeTextColor : subtleTextColor}
              >
                U23
              </Text>
            </HStack>

            <Select
              value={nationality}
              onChange={(event) => setNationality(event.target.value)}
              bg={panelBg}
              flex={{ base: "1 1 0", md: "0 0 auto" }}
              minW={0}
              maxW={{ base: "none", md: "240px" }}
            >
              <option value="All">All nations</option>
              {CONTINENT_ORDER.some(
                (c) => nationalitiesByContinent.grouped[c],
              ) && (
                <optgroup label="By continent">
                  {CONTINENT_ORDER.filter(
                    (c) => nationalitiesByContinent.grouped[c],
                  ).map((c) => (
                    <option key={`continent:${c}`} value={`continent:${c}`}>
                      {c}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="By Country">
                {nationalities.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.value}
                  </option>
                ))}
              </optgroup>
            </Select>

            <Select
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              bg={panelBg}
              flex={{ base: "1 1 0", md: "0 0 auto" }}
              minW={0}
              maxW={{ base: "none", md: "220px" }}
            >
              {dates.map((dateValue) => (
                <option key={dateValue} value={dateValue}>
                  {formatDateLabel(dateValue)}
                </option>
              ))}
            </Select>

            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search rider name"
              bg={panelBg}
              flex={{ base: "1 0 100%", md: "1 1 auto" }}
            />
          </Flex>

          <TableContainer
            bg={panelBg}
            borderRadius="20px"
            boxShadow="0 18px 40px rgba(15, 23, 42, 0.12)"
            border="1px solid"
            borderColor={panelBorderColor}
            maxH="70vh"
            overflowY="auto"
          >
            <Table size="sm">
              <Thead position="sticky" top={0} bg={tableHeaderBg} zIndex={10}>
                <Tr>
                  <Th
                    width="90px"
                    cursor="pointer"
                    onClick={() =>
                      setRankMode((prev) =>
                        prev === "relative" ? "absolute" : "relative",
                      )
                    }
                    _hover={{ color: "blue.500", textDecoration: "underline" }}
                  >
                    <Text
                      as="span"
                      fontSize="xs"
                      ml={2}
                      color={subtleTextColor}
                    >
                      Rank/{rankMode === "relative" ? "R" : "A"}
                    </Text>
                  </Th>
                  <Th width="80px">Nation</Th>
                  <Th>Rider</Th>
                  <Th width="120px">Born</Th>
                  <Th width="180px">Weekly Change</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  Array.from({ length: 12 }).map((_, index) => (
                    <Tr key={`skeleton-${index}`}>
                      <Td>
                        <Skeleton height="20px" width="40px" />
                      </Td>
                      <Td>
                        <Skeleton
                          height="24px"
                          width="24px"
                          borderRadius="2px"
                        />
                      </Td>
                      <Td>
                        <Skeleton height="20px" width="180px" />
                      </Td>
                      <Td>
                        <Skeleton height="20px" width="60px" />
                      </Td>
                      <Td>
                        <Skeleton height="20px" width="80px" />
                      </Td>
                    </Tr>
                  ))
                ) : rankedRiders.length ? (
                  rankedRiders.map((rider, index) => {
                    const nationalityKey = rider.nationality_ioc
                      ? rider.nationality_ioc.toLowerCase()
                      : "";
                    return (
                      <Tr
                        key={
                          rider.uci_id ||
                          `${rider.name}-${rider.nationality_ioc}-${index}`
                        }
                        _hover={{ bg: tableRowHoverBg }}
                      >
                        <Td>
                          <Text fontWeight="600">
                            {rankMode === "relative" ? index + 1 : rider.rank}
                          </Text>
                        </Td>
                        <Td>
                          {nationalityKey ? (
                            <Box
                              className="flag"
                              data-nationality={nationalityKey}
                              title={rider.nationality_name}
                            />
                          ) : (
                            <Box w="24px" h="24px" bg={placeholderFlagBg} />
                          )}
                        </Td>
                        <Td>
                          <Button
                            variant="link"
                            colorScheme="blue"
                            fontWeight="600"
                            onClick={() => handleRiderClick(rider)}
                          >
                            {rider.name}
                          </Button>
                        </Td>
                        <Td>{rider.birthyear || "-"}</Td>
                        <Td>
                          {rider.change === null ? (
                            <Text color={subtleTextColor}>-</Text>
                          ) : rider.change > 0 ? (
                            <HStack spacing={2} color="green.500">
                              <Icon as={TriangleUpIcon} />
                              <Text fontWeight="600">{rider.change}</Text>
                            </HStack>
                          ) : rider.change < 0 ? (
                            <HStack spacing={2} color="red.500">
                              <Icon as={TriangleDownIcon} />
                              <Text fontWeight="600">
                                {Math.abs(rider.change)}
                              </Text>
                            </HStack>
                          ) : (
                            <Text color={subtleTextColor}>&nbsp;</Text>
                          )}
                        </Td>
                      </Tr>
                    );
                  })
                ) : (
                  <Tr>
                    <Td colSpan={5}>
                      <Text color={subtleTextColor}>
                        No riders match the selected filters.
                      </Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>

          <Text color={subtleTextColor} fontSize="sm">
            Showing {rankedRiders.length} riders for{" "}
            {selectedDate ? formatDateLabel(selectedDate) : "the latest week"}.
          </Text>
        </Stack>
      </Container>

      <Drawer
        isOpen={drawer.isOpen}
        placement="right"
        onClose={drawer.onClose}
        size="xl"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            {selectedRider ? (
              <HStack spacing={3} align="center">
                {selectedRider.nationality_ioc ? (
                  <Box
                    className="flag flag--lg"
                    data-nationality={selectedRider.nationality_ioc.toLowerCase()}
                  />
                ) : (
                  <Box w="36px" h="36px" bg={placeholderFlagBg} />
                )}
                <Box minW={0}>
                  <Heading size="md" textAlign="left" noOfLines={1}>
                    {selectedRider.name}
                  </Heading>
                  <Text fontSize="sm" color={subtleTextColor} mt={0}>
                    {selectedNationalityChanges &&
                    selectedNationalityChanges.length
                      ? `(${selectedNationalityChanges
                          .map((ch, idx) =>
                            idx === 0
                              ? `${ch.current} - ${
                                  ch.previous
                                } until ${formatDateLabel(ch.date)}`
                              : `${ch.previous} until ${formatDateLabel(
                                  ch.date,
                                )}`,
                          )
                          .join("; ")})`
                      : selectedRider.nationality_ioc
                        ? `(${selectedRider.nationality_ioc})`
                        : ""}
                  </Text>
                </Box>
              </HStack>
            ) : (
              "Rider Snapshot"
            )}
          </DrawerHeader>
          <DrawerBody>
            {selectedRider ? (
              <Stack spacing={6}>
                <Flex
                  align="flex-start"
                  justify="space-between"
                  gap={4}
                  // At the narrowest sizes, present details on the left and image on the right
                  direction={{ base: "row", sm: "row" }}
                  wrap="nowrap"
                >
                  <Box
                    flex="1 1 50%"
                    minW="0"
                    maxW={{ base: "50%", sm: "auto" }}
                    pr={{ base: 3, sm: 0 }}
                  >
                    {/* header moved into DrawerHeader; keep spacing for content */}
                    <Box mb={3} />

                    <VStack align="start" spacing={3}>
                      <Stack
                        direction={{ base: "column", sm: "row" }}
                        spacing={1}
                        align="start"
                      >
                        <Text fontWeight="600">UCI ID:</Text>
                        <Text>{selectedRider.uci_id || "-"}</Text>
                      </Stack>

                      <Stack
                        direction={{ base: "column", sm: "row" }}
                        spacing={1}
                        align="start"
                      >
                        <Text fontWeight="600">Birth Year:</Text>
                        <Text>{selectedRider.birthyear || "-"}</Text>
                      </Stack>

                      <Stack
                        direction={{ base: "column", sm: "row" }}
                        spacing={1}
                        align="start"
                      >
                        <Text fontWeight="600">Best Ranking:</Text>
                        {(() => {
                          const bestRank = getBestRankInfo(selectedRider);
                          return (
                            <Text>
                              {bestRank.rank || "-"}
                              {bestRank.date
                                ? ` (${formatDateLabel(bestRank.date)})`
                                : ""}
                            </Text>
                          );
                        })()}
                      </Stack>

                      <Box w="100%" maxW={{ base: "180px", sm: "210px" }}>
                        <Text fontWeight="600" mb={2}>
                          Ranking Sparkline
                        </Text>
                        {getSparklineData(selectedRider).length > 1 ? (
                          <Box bg={sparklineBg} borderRadius="12px" p={3}>
                            <Sparklines
                              data={getSparklineData(selectedRider)}
                              width={sparklineWidth || 170}
                              height={60}
                              margin={6}
                            >
                              <SparklinesLine
                                color="#507db8"
                                style={{ fill: "none" }}
                              />
                            </Sparklines>
                          </Box>
                        ) : (
                          <Text color={subtleTextColor}>
                            Not enough data to render a sparkline.
                          </Text>
                        )}
                      </Box>
                    </VStack>
                  </Box>

                  {hasRiderImage ? (
                    <Box
                      flex="0 0 50%"
                      w={{ base: "50%", sm: "120px" }}
                      maxW={{ base: "50%", sm: "180px" }}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      alignSelf={{ base: "flex-start", sm: "flex-start" }}
                    >
                      <Image
                        src={riderImageUrl}
                        alt={`${selectedRider.name} profile`}
                        maxH={{ base: "240px", sm: "140px" }}
                        h="auto"
                        w="100%"
                        objectFit="contain"
                        borderRadius="10px"
                      />
                    </Box>
                  ) : null}
                </Flex>

                {recentHighlights.length ? (
                  <Box>
                    <Text fontWeight="600" mb={2}>
                      Recent Highlights
                    </Text>
                    <Stack
                      spacing={3}
                      border="1px solid"
                      borderColor={panelBorderColor}
                      borderRadius="12px"
                      p={4}
                    >
                      {recentHighlights.map((highlight) => (
                        <Flex
                          key={highlight.key}
                          align="center"
                          gap={3}
                          minW={0}
                          cursor="pointer"
                          _hover={{ bg: tableRowHoverBg }}
                          onClick={() => openResultModal(highlight.result)}
                        >
                          <HighlightMarker
                            type={highlight.type}
                            rank={highlight.rank}
                          />
                          <Box minW={0} flex="1">
                            <Text noOfLines={1}>
                              <Box as="span" fontWeight="600">
                                {highlight.result.competition_title ||
                                  "Untitled event"}
                              </Box>
                              <Box
                                as="span"
                                color={mutedTextColor}
                                fontSize="sm"
                                ml={3}
                              >
                                {simplifyEventLabel(highlight.result.event)} ·{" "}
                                {highlight.result.competition_city
                                  ? `${highlight.result.competition_city} · `
                                  : ""}
                                {highlight.result.competition_class || "-"} ·{" "}
                                {highlight.result.competition_dates || "-"}
                              </Box>
                            </Text>
                          </Box>
                        </Flex>
                      ))}
                    </Stack>
                  </Box>
                ) : null}

                <Box>
                  <Flex align="center" justify="space-between" mb={2}>
                    <HStack>
                      <Text fontSize="sm" color={subtleTextColor}>
                        Sort:
                      </Text>
                      <Select
                        size="sm"
                        value={resultSortOrder}
                        onChange={(e) => setResultSortOrder(e.target.value)}
                        width="160px"
                      >
                        <option value="desc">Newest first</option>
                        <option value="asc">Oldest first</option>
                      </Select>
                      <Checkbox
                        isChecked={showSprint}
                        onChange={(e) => setShowSprint(e.target.checked)}
                      >
                        Sprint
                      </Checkbox>
                      <Checkbox
                        isChecked={showKeirin}
                        onChange={(e) => setShowKeirin(e.target.checked)}
                      >
                        Keirin
                      </Checkbox>
                    </HStack>
                  </Flex>
                  {Array.isArray(selectedRider.results) &&
                  selectedRider.results.length ? (
                    <TableContainer
                      border="1px solid"
                      borderColor={panelBorderColor}
                      borderRadius="12px"
                    >
                      <Table size="sm" variant="simple">
                        <Thead
                          position="sticky"
                          top={0}
                          bg={tableHeaderBg}
                          zIndex={10}
                        >
                          <Tr>
                            <Th width="32px">&nbsp;</Th>
                            <Th>Competition</Th>
                            <Th width="120px">Dates</Th>
                            <Th width="80px">Event</Th>
                            <Th width="64px" isNumeric>
                              Rank
                            </Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {(() => {
                            const withDates = selectedRider.results.map(
                              (r, i) => ({
                                __origIndex: i,
                                data: r,
                                endDate: parseCompetitionEndDate(
                                  r.competition_dates ||
                                    r.competition_dates_text ||
                                    "",
                                ),
                              }),
                            );

                            const filteredWithDates = withDates.filter(
                              (wrapped) => {
                                const event = (
                                  wrapped.data.event || ""
                                ).toLowerCase();
                                const isSprint = event.includes("sprint");
                                const isKeirin = event.includes("keirin");
                                return (
                                  (!isSprint && !isKeirin) ||
                                  (isSprint && showSprint) ||
                                  (isKeirin && showKeirin)
                                );
                              },
                            );

                            filteredWithDates.sort((a, b) => {
                              const ta = a.endDate ? a.endDate.getTime() : null;
                              const tb = b.endDate ? b.endDate.getTime() : null;
                              if (ta === tb)
                                return a.__origIndex - b.__origIndex;
                              if (ta === null)
                                return resultSortOrder === "asc" ? -1 : 1;
                              if (tb === null)
                                return resultSortOrder === "asc" ? 1 : -1;
                              return resultSortOrder === "asc"
                                ? ta - tb
                                : tb - ta;
                            });

                            return filteredWithDates.map((wrapped, index) => {
                              const result = wrapped.data; // Extract result for clarity
                              const countryKey =
                                result.competition_country_ioc?.toLowerCase() ||
                                "";
                              return (
                                <Tr
                                  key={`${
                                    result.event_id || result.event
                                  }-${index}`}
                                  _hover={{ bg: tableRowHoverBg }}
                                  cursor="pointer"
                                  onClick={() => openResultModal(result)}
                                >
                                  <Td>
                                    {countryKey ? (
                                      <Box
                                        className="flag"
                                        data-nationality={countryKey}
                                        title={
                                          result.competition_country_ioc ||
                                          "Unknown"
                                        }
                                      />
                                    ) : (
                                      <Box
                                        w="24px"
                                        h="24px"
                                        bg={placeholderFlagBg}
                                      />
                                    )}
                                  </Td>

                                  <Td>
                                    <Box minW={0}>
                                      <HStack spacing={2} align="start">
                                        <Text
                                          fontWeight="600"
                                          fontSize="md"
                                          noOfLines={1}
                                        >
                                          {truncateText(
                                            result.competition_title ||
                                              "Untitled event",
                                            32,
                                          )}
                                        </Text>
                                        {result.competition_city && (
                                          <Text
                                            fontSize="xs"
                                            fontWeight="normal"
                                            color={mutedTextColor}
                                            noOfLines={1}
                                          >
                                            {truncateText(
                                              result.competition_city,
                                              20,
                                            )}
                                          </Text>
                                        )}
                                      </HStack>
                                      <Text
                                        fontSize="xs"
                                        color={mutedTextColor}
                                        mt={1}
                                      >
                                        {result.competition_class || "-"}
                                        <Box as="span" mx={2} display="inline">
                                          ·
                                        </Box>
                                        <Box
                                          as="span"
                                          display="inline-block"
                                          verticalAlign="middle"
                                        >
                                          {typeof result.event_stars ===
                                          "number" ? (
                                            <StarRating
                                              value={result.event_stars}
                                              max={5}
                                              size="xs"
                                            />
                                          ) : (
                                            <Text
                                              as="span"
                                              color={subtleTextColor}
                                              fontSize="xs"
                                            >
                                              -
                                            </Text>
                                          )}
                                        </Box>
                                      </Text>
                                    </Box>
                                  </Td>

                                  <Td width="120px">
                                    <Text
                                      fontSize="sm"
                                      color={mutedTextColor}
                                      noOfLines={1}
                                    >
                                      {result.competition_dates || "-"}
                                    </Text>
                                  </Td>

                                  <Td>{simplifyEventLabel(result.event)}</Td>

                                  <Td isNumeric>
                                    {result.rank ? (
                                      <Badge
                                        fontSize={result.rank > 9 ? "sm" : "lg"}
                                        px={result.rank > 9 ? 2 : 3}
                                        py={1}
                                        border="1px solid"
                                        bg={
                                          result.rank === 1
                                            ? "yellow.400"
                                            : result.rank === 2
                                              ? "gray.300"
                                              : result.rank === 3
                                                ? "yellow.700"
                                                : undefined
                                        }
                                        borderColor={
                                          result.rank === 1
                                            ? "yellow.500"
                                            : result.rank === 2
                                              ? "gray.400"
                                              : result.rank === 3
                                                ? "yellow.900"
                                                : rankBadgeFallbackBorderColor
                                        }
                                        color={
                                          result.rank === 1
                                            ? "black"
                                            : result.rank === 2
                                              ? "black"
                                              : result.rank === 3
                                                ? "white"
                                                : rankBadgeFallbackColor
                                        }
                                        fontWeight="700"
                                      >
                                        {result.rank}
                                      </Badge>
                                    ) : (
                                      "-"
                                    )}
                                  </Td>
                                </Tr>
                              );
                            });
                          })()}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Text color={subtleTextColor}>
                      No recorded event results for this rider yet.
                    </Text>
                  )}
                </Box>
              </Stack>
            ) : (
              <Skeleton />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <Modal
        isOpen={resultModalOpen}
        onClose={closeResultModal}
        size="xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent maxH="85vh" display="flex" flexDirection="column">
          <ModalHeader>
            <Box>
              <HStack spacing={3} align="center" mb={1}>
                {(() => {
                  const competitionCountry =
                    eventMeta?.competition?.country_ioc ||
                    selectedResult?.competition_country_ioc ||
                    "";
                  const countryKey = competitionCountry.toLowerCase();
                  return countryKey ? (
                    <Box
                      className="flag"
                      data-nationality={countryKey}
                      title={competitionCountry || "Unknown"}
                    />
                  ) : (
                    <Box w="24px" h="24px" bg={placeholderFlagBg} />
                  );
                })()}
                <Heading size="md" lineHeight="1.2">
                  {eventMeta?.competition?.title ||
                    selectedResult?.competition_title ||
                    "Event Result"}
                </Heading>
              </HStack>
              <HStack spacing={2} align="center">
                {(eventMeta?.competition?.city ||
                  selectedResult?.competition_city) && (
                  <>
                    <HStack spacing={1} align="center">
                      <MapPin size={16} color="#6B7280" />
                      <Text
                        fontSize="sm"
                        fontWeight="normal"
                        color={mutedTextColor}
                      >
                        {eventMeta?.competition?.city ||
                          selectedResult?.competition_city}
                      </Text>
                    </HStack>
                    <Text fontSize="sm" color={mutedTextColor}>
                      {" · "}
                    </Text>
                  </>
                )}
                <Text fontSize="sm" color={mutedTextColor} fontWeight="normal">
                  {eventMeta?.competition?.class ||
                    selectedResult?.competition_class ||
                    "-"}
                </Text>
                <Text fontSize="sm" color={mutedTextColor}>
                  {" · "}
                </Text>
                <Text fontSize="sm" color={mutedTextColor} fontWeight="normal">
                  {eventMeta?.competition?.dates ||
                    selectedResult?.competition_dates ||
                    "-"}
                </Text>
              </HStack>
            </Box>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            display="flex"
            flexDirection="column"
            minH={0}
            flex="1"
            sx={{ touchAction: "pan-y" }}
          >
            <Box mb={4}>
              <HStack spacing={3} align="center" mb={2}>
                <Heading size="sm" lineHeight="1.2">
                  {eventMeta?.event?.name || selectedResult?.event || "-"}
                </Heading>
                {typeof (
                  eventMeta?.event?.stars ?? selectedResult?.event_stars
                ) === "number" ? (
                  <StarRating
                    value={
                      eventMeta?.event?.stars ?? selectedResult?.event_stars
                    }
                    max={5}
                    size="sm"
                  />
                ) : null}
              </HStack>
            </Box>
            {eventLoading ? (
              <Text>Loading event results…</Text>
            ) : eventRiders.length ? (
              <Box
                ref={modalBodyRef}
                border="1px solid"
                borderColor={panelBorderColor}
                borderRadius="12px"
                flex="1"
                minH={0}
                overflowY="auto"
                overflowX="hidden"
                overscrollBehavior="contain"
                sx={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
                tabIndex={0}
                role="region"
                aria-label="Event results table"
                onWheel={(event) => event.stopPropagation()}
                onTouchStart={onModalTouchStart}
                onTouchMove={onModalTouchMove}
                onTouchEnd={onModalTouchEnd}
                onTouchCancel={onModalTouchEnd}
                onKeyDown={(event) => {
                  const el = event.currentTarget;
                  const step = 48;
                  const page = Math.max(120, el.clientHeight - 40);
                  if (event.key === "ArrowDown") {
                    event.preventDefault();
                    el.scrollTop += step;
                  } else if (event.key === "ArrowUp") {
                    event.preventDefault();
                    el.scrollTop -= step;
                  } else if (event.key === "PageDown") {
                    event.preventDefault();
                    el.scrollTop += page;
                  } else if (event.key === "PageUp") {
                    event.preventDefault();
                    el.scrollTop -= page;
                  } else if (event.key === "Home") {
                    event.preventDefault();
                    el.scrollTop = 0;
                  } else if (event.key === "End") {
                    event.preventDefault();
                    el.scrollTop = el.scrollHeight;
                  }
                }}
              >
                <Table size="sm">
                  <Thead
                    position="sticky"
                    top={0}
                    bg={tableHeaderBg}
                    zIndex={1}
                  >
                    <Tr>
                      <Th width="80px">Rank</Th>
                      <Th width="130px">Nation</Th>
                      <Th>Rider</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {eventRiders.map((r) => {
                      const riderCountry = (r.nationality || "").toLowerCase();
                      return (
                        <Tr
                          key={`${r.uci_id}-${r.rank}-${r.name}`}
                          data-uci-id={r.uci_id}
                          bg={
                            selectedRider &&
                            String(r.uci_id) === String(selectedRider.uci_id)
                              ? selectedRowBg
                              : undefined
                          }
                          fontWeight={
                            selectedRider &&
                            String(r.uci_id) === String(selectedRider.uci_id)
                              ? "600"
                              : undefined
                          }
                        >
                          <Td>{r.rank}</Td>
                          <Td>
                            <HStack spacing={3} align="center">
                              {riderCountry && riderCountry !== "***" ? (
                                <Box
                                  className="flag"
                                  data-nationality={riderCountry}
                                  title={r.nationality || "Unknown"}
                                />
                              ) : (
                                <Box w="24px" h="24px" bg={placeholderFlagBg} />
                              )}
                              <Text>{r.nationality || "-"}</Text>
                            </HStack>
                          </Td>
                          <Td>{r.name}</Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <Text color={subtleTextColor}>
                No full event results available.
              </Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default App;
